import { Box, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import { isMobileNavItemActive, isPrimaryNavigationDisabled, navItems } from "./navigation";
import { NavItemIcon } from "./NavItemIcon";
import { useRememberedRoute } from "@/features/preferences/page-state";

export const DesktopNavigationList = ({
  onboardingCompleted,
  isAnalyzing,
}: {
  onboardingCompleted: boolean;
  isAnalyzing: boolean;
}) => {
  const { pathname } = useLocation();
  const rememberedRoutes: Record<string, string> = {
    "/games": useRememberedRoute("/games"),
    "/patterns": useRememberedRoute("/patterns"),
    "/trends": useRememberedRoute("/trends"),
  };
  return (
    <List
      data-testid="sidebar-navigation"
      sx={{
        flex: 1,
        minHeight: 0,
        pt: 0,
        overflowY: "auto",
        overscrollBehavior: "contain",
        scrollBehavior: "smooth",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {navItems.map((item, index) => {
        const isDisabled = isPrimaryNavigationDisabled(onboardingCompleted);
        const active = isMobileNavItemActive(pathname, item.path);
        return (
          <ListItemButton
            key={item.path}
            component={NavLink}
            to={rememberedRoutes[item.path] ?? item.path}
            className={active ? "active" : undefined}
            aria-current={active ? "page" : undefined}
            disabled={isDisabled}
            disableRipple
            sx={{
              borderRadius: 0,
              borderTop: index === 0 ? "none" : "2px solid transparent",
              borderBottom: "2px solid transparent",
              boxSizing: "border-box",
              transition: "none",
              opacity: isDisabled ? 0.5 : 1,
              cursor: isDisabled ? "not-allowed" : "pointer",
              pointerEvents: isDisabled ? "none" : "auto",
              "&.active": {
                bgcolor: "primary.main",
                color: "background.default",
                borderTopColor: index === 0 ? "transparent" : "primary.main",
                borderBottomColor: "primary.main",
                "& .MuiListItemIcon-root": { color: "background.default" },
              },
              "&:hover": { bgcolor: !isDisabled ? "rgba(240,236,226,0.05)" : "transparent" },
              "&.active:hover": { bgcolor: "primary.main" },
            }}
          >
            <ListItemIcon sx={{ color: "text.secondary", minWidth: 36 }}>
              <NavItemIcon icon={item.icon} />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: 700,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
              }}
            />
            {item.path === "/analyze" && isAnalyzing && !isDisabled && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "currentColor",
                  flexShrink: 0,
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
                }}
              />
            )}
          </ListItemButton>
        );
      })}
    </List>
  );
};
